import React, {useEffect, useState} from "react";
import {Button, Card, message, Popover, Row, Table, Tooltip} from "antd";
import {Link} from "react-router-dom";
import JSONInput from "react-json-editor-ajrm";

import {b64DecodeToUrl, formatMomentDate} from "../../../../../utils/helpers";
import {useFetch} from "../../../../../hooks/useFetch";
import {useStore} from "../../../../../hooks/useStore";
import AllCourierWalletsTabFilters from "./AllCourierWalletsTabFilters";
import moment from "moment";
import {InfoCircleOutlined} from "@ant-design/icons";
import {formatMoney} from "../../../../../utils/moneyHelper";
import {UNPROCESSED_PERIOD_EARNING_TOOLTIP} from "../../../../../constants/constants";

const PAGE_SIZE = 15;

export const AllCourierWalletsTab = ({isTabOpened}) => {
    const stores = useStore();
    const [filters, setFilters] = useState({page: 1, city: "all"});
    const [loadingExcelData, setLoadingExcelData] = useState(false);
    const [loading, terminals] = useFetch({
        fetchFn: stores.walletStore.listWalletCouriers.bind(null, {
            ...{
                ...filters,
                isWalletActive:
                    filters.isWalletActive === undefined
                        ? undefined
                        : filters.isWalletActive === "true" || filters.isWalletActive === true
            },
            skip: (filters.page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            excel: false
        }),
        dependencies: [filters, isTabOpened],
        waitForResponse: true,
        condition: isTabOpened
    });

    const [walletInvoiceVendorResultLoading, walletInvoiceVendorResult] = useFetch({
        fetchFn: stores.walletStore.getWalletInvoiceVendors.bind(null, {}),
        dependencies: [],
        initialCall: true
    });

    const columns = [
        {
            title: "Kurye",
            dataIndex: ["courier", "fullname"],
            width: 180,
            key: "courier.fullname",
            render: (fullname, row) =>
                row?.courier ? (
                    stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                        <Link
                            target="_blank"
                            to={{
                                pathname: "/couriers/" + row?.courier?._id
                            }}
                        >
                            {row?.courier?.fullname}
                        </Link>
                    ) : (
                        row?.courier?.fullname ?? "Kurye"
                    )
                ) : (
                    "-"
                )
        },
        {
            title: "TCKN",
            dataIndex: ["courier", "tckn"],
            key: "tckn",
            width: 150,
            render: tckn =>
                stores.meStore.checkRole("notVisibleForCompanyUser") ? <>{tckn}</> : <>-</>
        },
        {
            title: "Telefon",
            dataIndex: ["courier", "cellPhone"],
            key: "cellPhone",
            width: 150,
            render: cellPhone =>
                stores.meStore.checkRole("notVisibleForCompanyUser") ? <>{cellPhone}</> : <>-</>
        },
        {
            title: "Şehir",
            dataIndex: ["courier", "city"],
            key: "city",
            width: 100,
            render: city => <>{city?.capitalize() || city}</>
        },
        {
            title: "Bakiye",
            dataIndex: "balance",
            key: "balance",
            width: 150,
            render: (balance, row) => (
                <>
                    {formatMoney({amount: balance})}

                    <Tooltip
                        title={
                            <div style={{width: "200px"}}>
                                <div style={{display: "flex", justifyContent: "space-between"}}>
                                    <b>Bakiye : </b>
                                    {formatMoney({amount: balance})}
                                </div>
                                <div style={{display: "flex", justifyContent: "space-between"}}>
                                    <b>Bloke Bakiye : </b>
                                    {formatMoney({amount: row?.blockedBalance})}
                                </div>
                            </div>
                        }
                    >
                        <InfoCircleOutlined style={{marginLeft: 4, color: "#797979"}} />
                    </Tooltip>
                </>
            )
        },
        {
            title: "Bloke Bakiye",
            dataIndex: "blockedBalance",
            key: "blockedBalance",
            width: 150,
            render: blockedBalance => formatMoney({amount: blockedBalance})
        },
        {
            title: "Çekilebilir Tutar",
            dataIndex: "withdrawableAmount",
            key: "withdrawableAmount",
            width: 170,
            render: withdrawableAmount => formatMoney({amount: withdrawableAmount})
        },
        {
            title: "Nakit Alınabilir Sipariş Tutarı",
            dataIndex: "cashOnDeliveryLimit",
            key: "cashOnDeliveryLimit",
            width: 260,
            render: cashOnDeliveryLimit => formatMoney({amount: cashOnDeliveryLimit})
        },
        {
            title: "Nakit Alınabilir Sipariş Tutar Limiti",
            dataIndex: "cashOnDeliveryLimitOffset",
            key: "cashOnDeliveryLimitOffset",
            width: 290,
            render: cashOnDeliveryLimitOffset => formatMoney({amount: cashOnDeliveryLimitOffset})
        },
        {
            title: (
                <>
                    İşlenmemiş Dönem Kazancı
                    <Tooltip title={UNPROCESSED_PERIOD_EARNING_TOOLTIP}>
                        <InfoCircleOutlined style={{marginLeft: 4, color: "#797979"}} />
                    </Tooltip>
                </>
            ),
            dataIndex: "unprocessedEarning",
            key: "unprocessedEarning",
            width: 220,
            render: unprocessedEarning => formatMoney({amount: unprocessedEarning})
        },
        {
            title: "Entegratör",
            width: 90,
            dataIndex: ["courierInvoiceAccount", "vendor"],
            key: "vendor",
            render: (vendor, row) => {
                const courierVendor = vendor || row?.courier?.vendor;
                const vendorLabel =
                    courierVendor &&
                    (walletInvoiceVendorResult?.result?.vendors || [])?.find(
                        vendor => vendor?.code === courierVendor
                    )?.label;

                return vendorLabel ? vendorLabel : courierVendor ? courierVendor : "-";
            }
        },
        {
            title: <>Entegratör Durumu</>,
            width: 160,
            dataIndex: ["courierInvoiceAccount", "status"],
            key: "courierInvoiceAccount.status",
            render: (status, row) => (
                <>
                    {status ? status : "-"}
                    {row?.courierInvoiceAccount?.metaData && (
                        <Popover
                            content={
                                <JSONInput
                                    id="courierInvoiceAccountJSON"
                                    placeholder={row?.courierInvoiceAccount?.metaData}
                                    theme="dark_mitsuketa_tribute"
                                    width="auto"
                                    height="auto"
                                    viewOnly={true}
                                    confirmGood={false}
                                />
                            }
                        >
                            <InfoCircleOutlined style={{marginLeft: 4, color: "#797979"}} />
                        </Popover>
                    )}
                </>
            )
        },
        {
            title: <>Cüzdan Oluşturulma Tarihi</>,
            dataIndex: "createdAt",
            key: "createdAt",
            width: 200,
            ellipsis: {
                showTitle: false
            },
            render: createdAt => (
                <Tooltip title={formatMomentDate(createdAt)}>
                    {moment(createdAt).format("lll")}
                </Tooltip>
            )
        },

        {
            title: "Durumu",
            width: 150,
            dataIndex: ["courier", "isWalletActive"],
            align: "center",
            key: "courier.isWalletActive",
            render: isWalletActive => (isWalletActive ? "Aktif" : "Pasif")
        },
        {
            title: "Deaktivasyon Nedeni",
            width: 200,
            dataIndex: ["walletDeactivatedInfo", "walletDeactivatedReason"],
            key: "walletDeactivatedReason",
            render: (walletDeactivatedReason, row) =>
                row?.courier?.isWalletActive ? "-" : walletDeactivatedReason || "-"
        },
        {
            title: "Deaktive Eden",
            width: 180,
            dataIndex: ["walletDeactivatedInfo", "walletDeactivatedBy", "fullName"],
            key: "walletDeactivatedBy",
            render: (fullName, row) =>
                row?.courier?.isWalletActive ? "-" : fullName || "Bilinmiyor"
        },
        {
            title: "Deaktivasyon Tarihi",
            width: 180,
            dataIndex: ["walletDeactivatedInfo", "walletDeactivatedAt"],
            key: "walletDeactivatedAt",
            render: (walletDeactivatedAt, row) =>
                row?.courier?.isWalletActive
                    ? "-"
                    : walletDeactivatedAt
                    ? moment(walletDeactivatedAt).format("YYYY-MM-DD HH:mm:ss")
                    : "-"
        },
        {
            title: "Aktive Eden",
            width: 180,
            dataIndex: ["walletActivatedInfo", "walletActivatedBy", "fullName"],
            key: "walletActivatedBy",
            render: (fullName, row) =>
                !row?.courier?.isWalletActive ? "-" : fullName || "Bilinmiyor"
        },
        {
            title: "Aktivasyon Tarihi",
            width: 180,
            dataIndex: ["walletActivatedInfo", "walletActivatedAt"],
            key: "walletActivatedAt",
            render: (walletActivatedAt, row) =>
                !row?.courier?.isWalletActive
                    ? "-"
                    : walletActivatedAt
                    ? moment(walletActivatedAt).format("YYYY-MM-DD HH:mm:ss")
                    : "-"
        }
    ];

    const getExcelExport = async () => {
        const messageKey = "all-wallet-couriers-excel";
        message.loading({content: "Excel hazırlanıyor...", key: messageKey});
        setLoadingExcelData(true);
        try {
            const response = await stores.walletStore.listWalletCouriers({
                ...filters,
                excel: true
            });

            const base64Data = response?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", "kurye_cuzdan_listesi.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success({
                content: "Excel dosyası başarıyla indirildi.",
                key: messageKey,
                duration: 4
            });
        } catch (error) {
            console.log("error", error);
            message.error({
                content: "Excel dosyası indirilirken bir hata oluştu.",
                key: messageKey,
                duration: 4
            });
        } finally {
            setLoadingExcelData(false);
        }
    };

    return (
        <Card>
            <Row gutter={[16, 16]} style={{marginBottom: 8}}>
                <AllCourierWalletsTabFilters
                    filters={filters}
                    setFilters={setFilters}
                    options={{
                        invoiceVendors: {
                            loading: walletInvoiceVendorResultLoading,
                            data: walletInvoiceVendorResult?.result
                        }
                    }}
                />
            </Row>
            {stores.meStore.checkPermission("walletCouriers.export") && (
                <Row gutter={[16, 16]} style={{marginBottom: 8}} justify="end">
                    <Button type="primary" onClick={getExcelExport} loading={loadingExcelData}>
                        {loadingExcelData ? "Excel Hazırlanıyor..." : "Excel İndir"}
                    </Button>
                </Row>
            )}
            <Table
                loading={loading}
                columns={columns}
                size="small"
                dataSource={
                    terminals?.rows?.map(row => ({
                        ...row,
                        rowKey: `${row?._id}-${row?.courier?._id}-${row?.courierInvoiceAccount?._id}`
                    })) || []
                }
                rowKey={"rowKey"}
                tableLayout="fixed"
                scroll={{x: 2000}}
                pagination={{
                    total: terminals?.dataCount || 0,
                    current: filters.page,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    pageSize: PAGE_SIZE,
                    onChange: page => {
                        setFilters({...filters, page});
                    }
                }}
            />
        </Card>
    );
};
